// Copyright (c) Ping. All rights reserved.

import type React from 'react';
import { useCallback } from 'react';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { createTheme } from '../../design-system/theme';
import { PollOptionRow } from './PollOptionRow';
import { pollStrings } from './strings';
import {
  POLL_MAX_OPTIONS,
  POLL_MIN_OPTIONS,
  POLL_QUESTION_MAX_LEN,
  type PollComposerState,
  type PollComposerValidation,
  type PollDurationPreset,
  type PollKind,
} from './types';
import { usePollComposerViewModel } from './usePollComposerViewModel';

export interface PollComposerScreenSubmitPayload {
  readonly state: PollComposerState;
}

export interface PollComposerScreenProps {
  readonly onCancel?: (() => void) | undefined;
  readonly onSubmit: (payload: PollComposerScreenSubmitPayload) => void | Promise<void>;
}

const DURATION_OPTIONS: readonly PollDurationPreset[] = ['1h', '24h', '7d', 'never'];

/**
 * Full-screen composer for creating a poll. Pure presentation; submission
 * is delegated to the host via {@link PollComposerScreenProps.onSubmit}.
 */
export const PollComposerScreen: React.FC<PollComposerScreenProps> = ({ onCancel, onSubmit }) => {
  const scheme = useColorScheme();
  const theme = createTheme(scheme === 'dark' ? 'dark' : 'light');
  const vm = usePollComposerViewModel();

  const handleSubmit = useCallback(() => {
    if (!vm.validation.canSubmit) {
      return;
    }
    void onSubmit({ state: vm.state });
  }, [vm.validation.canSubmit, vm.state, onSubmit]);

  const handleAddOption = useCallback(() => {
    if (vm.state.options.length >= POLL_MAX_OPTIONS) {
      return;
    }
    vm.addOption();
  }, [vm]);

  return (
    <View
      style={[styles.container, { backgroundColor: theme.colors.surface.primary }]}
      accessibilityLabel={pollStrings.composerTitle}
    >
      <View style={[styles.header, { borderBottomColor: theme.colors.border.secondary }]}>
        {onCancel !== undefined ? (
          <Pressable
            onPress={onCancel}
            accessibilityRole="button"
            accessibilityLabel={pollStrings.composerCancel}
            style={({ pressed }) => [pressed && { opacity: 0.6 }]}
          >
            <Text style={[styles.headerAction, { color: theme.colors.text.secondary }]}>
              {pollStrings.composerCancel}
            </Text>
          </Pressable>
        ) : (
          <View style={styles.headerSpacer} />
        )}
        <Text style={[styles.headerTitle, { color: theme.colors.text.primary }]}>
          {pollStrings.composerTitle}
        </Text>
        <Pressable
          onPress={handleSubmit}
          disabled={!vm.validation.canSubmit}
          accessibilityRole="button"
          accessibilityState={{ disabled: !vm.validation.canSubmit }}
          accessibilityLabel={pollStrings.composerSubmit}
          style={({ pressed }) => [pressed && { opacity: 0.6 }]}
        >
          <Text
            style={[
              styles.headerActionPrimary,
              {
                color: vm.validation.canSubmit
                  ? theme.colors.accent.indigo
                  : theme.colors.text.tertiary,
              },
            ]}
          >
            {pollStrings.composerSubmit}
          </Text>
        </Pressable>
      </View>

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        <TextInput
          value={vm.state.question}
          placeholder={pollStrings.questionPlaceholder}
          placeholderTextColor={theme.colors.text.tertiary}
          onChangeText={vm.setQuestion}
          maxLength={POLL_QUESTION_MAX_LEN}
          multiline
          accessibilityLabel={pollStrings.questionPlaceholder}
          style={[
            styles.questionInput,
            {
              color: theme.colors.text.primary,
              borderBottomColor: theme.colors.border.secondary,
            },
          ]}
        />

        <View style={styles.optionsBlock}>
          {vm.state.options.map((opt, idx) => (
            <PollOptionRow
              key={opt.id}
              index={idx}
              text={opt.text}
              mode={{
                kind: 'composer',
                removable: vm.state.options.length > POLL_MIN_OPTIONS,
              }}
              onChangeText={(text) => vm.setOptionText(opt.id, text)}
              onRemove={() => vm.removeOption(opt.id)}
            />
          ))}
          {vm.state.options.length < POLL_MAX_OPTIONS && (
            <Pressable
              onPress={handleAddOption}
              accessibilityRole="button"
              accessibilityLabel={pollStrings.addOption}
              style={({ pressed }) => [styles.addButton, pressed && { opacity: 0.6 }]}
            >
              <Text style={[styles.addButtonText, { color: theme.colors.accent.indigo }]}>
                + {pollStrings.addOption}
              </Text>
            </Pressable>
          )}
        </View>

        <KindToggleRow kind={vm.state.kind} setKind={vm.setKind} />

        <View style={[styles.toggleRow, { borderTopColor: theme.colors.border.secondary }]}>
          <Text style={[styles.toggleLabel, { color: theme.colors.text.primary }]}>
            {pollStrings.anonymousLabel}
          </Text>
          <Switch
            value={vm.state.anonymous}
            onValueChange={vm.setAnonymous}
            accessibilityLabel={pollStrings.anonymousLabel}
          />
        </View>

        <DurationRow duration={vm.state.duration} setDuration={vm.setDuration} />

        <ValidationFooter validation={vm.validation} />
      </ScrollView>
    </View>
  );
};

const KindToggleRow: React.FC<{
  readonly kind: PollKind;
  readonly setKind: (k: PollKind) => void;
}> = ({ kind, setKind }) => {
  const scheme = useColorScheme();
  const theme = createTheme(scheme === 'dark' ? 'dark' : 'light');
  return (
    <View style={[styles.toggleRow, { borderTopColor: theme.colors.border.secondary }]}>
      <Text style={[styles.toggleLabel, { color: theme.colors.text.primary }]}>
        {pollStrings.kindMultiple}
      </Text>
      <Switch
        value={kind === 'multiple'}
        onValueChange={(value) => setKind(value ? 'multiple' : 'single')}
        accessibilityLabel={pollStrings.kindMultiple}
      />
    </View>
  );
};

const DurationRow: React.FC<{
  readonly duration: PollDurationPreset;
  readonly setDuration: (d: PollDurationPreset) => void;
}> = ({ duration, setDuration }) => {
  const scheme = useColorScheme();
  const theme = createTheme(scheme === 'dark' ? 'dark' : 'light');
  return (
    <View style={[styles.durationBlock, { borderTopColor: theme.colors.border.secondary }]}>
      <Text style={[styles.toggleLabel, { color: theme.colors.text.primary }]}>
        {pollStrings.durationLabel}
      </Text>
      <View style={styles.durationChips}>
        {DURATION_OPTIONS.map((preset) => {
          const selected = preset === duration;
          return (
            <Pressable
              key={preset}
              onPress={() => setDuration(preset)}
              accessibilityRole="button"
              accessibilityLabel={pollStrings.durationPreset(preset)}
              accessibilityState={{ selected }}
              style={({ pressed }) => [
                styles.durationChip,
                {
                  borderColor: selected
                    ? theme.colors.accent.indigo
                    : theme.colors.border.secondary,
                  backgroundColor: selected ? `${theme.colors.accent.indigo}1A` : 'transparent',
                  opacity: pressed ? 0.7 : 1,
                },
              ]}
            >
              <Text
                style={[
                  styles.durationChipText,
                  {
                    color: selected ? theme.colors.accent.indigo : theme.colors.text.primary,
                  },
                ]}
              >
                {pollStrings.durationPreset(preset)}
              </Text>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
};

const ValidationFooter: React.FC<{ readonly validation: PollComposerValidation }> = ({
  validation,
}) => {
  const scheme = useColorScheme();
  const theme = createTheme(scheme === 'dark' ? 'dark' : 'light');
  if (validation.canSubmit || validation.errors.length === 0) {
    return null;
  }
  return (
    <View style={styles.errorBlock}>
      <Text style={[styles.errorTitle, { color: theme.colors.accent.coral }]}>
        {pollStrings.composerErrorPrefix}
      </Text>
      {validation.errors.map((e) => (
        <Text key={e} style={[styles.errorBody, { color: theme.colors.text.secondary }]}>
          • {pollStrings.composerError(e)}
        </Text>
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  } satisfies ViewStyle,
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
  } satisfies ViewStyle,
  headerSpacer: {
    minWidth: 60,
  } satisfies ViewStyle,
  headerTitle: {
    fontSize: 17,
    fontWeight: '600',
  } satisfies TextStyle,
  headerAction: {
    fontSize: 15,
    fontWeight: '500',
    minWidth: 60,
  } satisfies TextStyle,
  headerActionPrimary: {
    fontSize: 15,
    fontWeight: '700',
    minWidth: 60,
    textAlign: 'right',
  } satisfies TextStyle,
  scroll: {
    flex: 1,
  } satisfies ViewStyle,
  scrollContent: {
    padding: 16,
  } satisfies ViewStyle,
  questionInput: {
    fontSize: 18,
    fontWeight: '500',
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
    marginBottom: 16,
    minHeight: 48,
  } satisfies TextStyle,
  optionsBlock: {
    marginBottom: 16,
  } satisfies ViewStyle,
  addButton: {
    paddingVertical: 8,
    paddingHorizontal: 4,
  } satisfies ViewStyle,
  addButtonText: {
    fontSize: 14,
    fontWeight: '600',
  } satisfies TextStyle,
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    borderTopWidth: StyleSheet.hairlineWidth,
  } satisfies ViewStyle,
  toggleLabel: {
    flex: 1,
    fontSize: 15,
    fontWeight: '500',
    marginRight: 12,
  } satisfies TextStyle,
  durationBlock: {
    paddingVertical: 12,
    borderTopWidth: StyleSheet.hairlineWidth,
  } satisfies ViewStyle,
  durationChips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 10,
  } satisfies ViewStyle,
  durationChip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 14,
    borderWidth: 1,
    marginRight: 8,
    marginBottom: 8,
  } satisfies ViewStyle,
  durationChipText: {
    fontSize: 13,
    fontWeight: '600',
  } satisfies TextStyle,
  errorBlock: {
    marginTop: 16,
  } satisfies ViewStyle,
  errorTitle: {
    fontSize: 13,
    fontWeight: '700',
    marginBottom: 4,
  } satisfies TextStyle,
  errorBody: {
    fontSize: 13,
    paddingVertical: 1,
  } satisfies TextStyle,
});
